import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Sheet, SheetTrigger, SheetContent, SheetClose } from './ui/sheet';
import { Button } from './ui/button';
import { Menu, ChevronDown, ChevronUp, User, Heart, Sparkles, LayoutDashboard, LogOut } from 'lucide-react';

/**
 * Reusable mobile navigation drawer.
 *
 * - Height is capped to the (dynamic) viewport; the list scrolls with native touch gestures.
 * - The drawer header (with the Close button) is sticky and always accessible.
 * - Top/bottom scroll indicators appear when more items exist above/below the fold.
 * - Underlying page scroll is locked while open (Radix already does this; we belt-and-suspenders it).
 * - Fully config-driven: it renders whatever `nav` is passed (from the Admin site-config source of
 *   truth), so new verticals appear automatically with no frontend change.
 */
export const MobileNav = ({ nav = [], user, onLogout }) => {
  const [open, setOpen] = useState(false);
  const scrollRef = useRef(null);
  const [atTop, setAtTop] = useState(true);
  const [atBottom, setAtBottom] = useState(true);

  const recompute = () => {
    const el = scrollRef.current;
    if (!el) return;
    setAtTop(el.scrollTop <= 4);
    setAtBottom(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
  };

  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(recompute, 60);
    // Belt-and-suspenders body scroll lock.
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { clearTimeout(t); document.body.style.overflow = prev; };
  }, [open]);

  const itemCls = 'block px-3 py-3 rounded-lg font-medium text-foreground hover:bg-secondary active:bg-secondary/80 transition-colors';

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden text-primary-foreground hover:bg-white/10 shrink-0" data-testid="mobile-menu-trigger" aria-label="Open menu">
          <Menu className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      <SheetContent
        side="right"
        className="w-[86%] max-w-sm p-0 flex flex-col h-[100dvh] max-h-[100dvh]"
        data-testid="mobile-nav-drawer"
      >
        {/* Sticky header — brand + (the built-in Close X stays pinned top-right of the panel). */}
        <div className="flex items-center h-14 px-4 border-b border-border bg-background shrink-0">
          <span className="font-display font-semibold text-base">FlightDeals<span className="text-accent">.in</span></span>
        </div>

        {/* Scrollable navigation region. */}
        <div className="relative flex-1 min-h-0">
          {/* top indicator */}
          <div className={`pointer-events-none absolute inset-x-0 top-0 z-10 h-8 bg-gradient-to-b from-background to-transparent transition-opacity duration-200 ${atTop ? 'opacity-0' : 'opacity-100'}`} />
          {!atTop && (
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-1 z-20 text-muted-foreground/70">
              <ChevronUp className="h-4 w-4" />
            </div>
          )}

          <nav
            ref={scrollRef}
            onScroll={recompute}
            className="h-full overflow-y-auto overscroll-contain px-3 py-4 space-y-1"
            data-testid="mobile-nav-scroll"
          >
            {nav.map((n) => (
              <SheetClose asChild key={n.to}>
                <Link to={n.to} className={itemCls} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}>
                  {n.label}
                </Link>
              </SheetClose>
            ))}

            <div className="h-px bg-border my-2" />

            {user ? (
              <>
                <SheetClose asChild>
                  <Link to="/account" className={itemCls} data-testid="mobile-nav-account"><span className="inline-flex items-center gap-2"><Heart className="h-4 w-4" /> My Account</span></Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/creator/dashboard" className={itemCls} data-testid="mobile-nav-creator"><span className="inline-flex items-center gap-2"><Sparkles className="h-4 w-4" /> Creator Studio</span></Link>
                </SheetClose>
                {user.is_admin && (
                  <SheetClose asChild>
                    <Link to="/admin" className={itemCls} data-testid="mobile-nav-admin"><span className="inline-flex items-center gap-2"><LayoutDashboard className="h-4 w-4" /> Admin</span></Link>
                  </SheetClose>
                )}
                <SheetClose asChild>
                  <button onClick={onLogout} className={`${itemCls} w-full text-left`} data-testid="mobile-nav-logout"><span className="inline-flex items-center gap-2"><LogOut className="h-4 w-4" /> Logout</span></button>
                </SheetClose>
              </>
            ) : (
              <>
                <SheetClose asChild>
                  <Link to="/login" className={`${itemCls} bg-accent/10 text-accent hover:bg-accent/15`} data-testid="mobile-nav-signin"><span className="inline-flex items-center gap-2"><User className="h-4 w-4" /> Sign in</span></Link>
                </SheetClose>
                <SheetClose asChild>
                  <Link to="/creators/join" className={itemCls} data-testid="mobile-nav-join">Become a creator</Link>
                </SheetClose>
              </>
            )}
            {/* trailing spacer so the last item clears the bottom fade */}
            <div className="h-6" />
          </nav>

          {/* bottom indicator */}
          <div className={`pointer-events-none absolute inset-x-0 bottom-0 z-10 h-10 bg-gradient-to-t from-background to-transparent transition-opacity duration-200 ${atBottom ? 'opacity-0' : 'opacity-100'}`} />
          {!atBottom && (
            <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-1 z-20 text-muted-foreground/70 animate-bounce">
              <ChevronDown className="h-4 w-4" />
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
